import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatMartiniqueDate } from "@/lib/api";
import { Sailboat, ArrowRight, Timer, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCountdown } from "@/hooks/useCountdown";

const STATUS_LABEL = {
  upcoming: { text: "Ouvert", cls: "bg-[color:var(--palm)] text-[color:var(--deep)]" },
  closed: { text: "Fermé", cls: "bg-amber-100 text-amber-700" },
  settled: { text: "Réglé", cls: "bg-slate-200 text-slate-600" },
};

export default function Stages() {
  const [stages, setStages] = useState([]);
  const [tab, setTab] = useState("upcoming");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/stages");
      setStages(data);
    })();
  }, []);

  const filtered = stages.filter((s) => s.status === tab);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[color:var(--deep)]">
          Les étapes
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Choisis une étape pour placer un pari.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 rounded-full h-auto" data-testid="stages-tabs">
          <TabsTrigger
            value="upcoming"
            data-testid="tab-upcoming"
            className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]"
          >
            Ouvertes ({stages.filter((s) => s.status === "upcoming").length})
          </TabsTrigger>
          <TabsTrigger
            value="closed"
            data-testid="tab-closed"
            className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]"
          >
            Fermées ({stages.filter((s) => s.status === "closed").length})
          </TabsTrigger>
          <TabsTrigger
            value="settled"
            data-testid="tab-settled"
            className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]"
          >
            Réglées ({stages.filter((s) => s.status === "settled").length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6 space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
              <Sailboat className="h-8 w-8 mx-auto text-slate-300 mb-3" />
              <div className="font-display font-bold text-lg text-[color:var(--deep)]">Rien ici pour le moment</div>
            </div>
          ) : (
            filtered.map((s, i) => (
              <StageRow key={s.id} s={s} i={i} />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StageRow({ s, i }) {
  const cd = useCountdown(s.date);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.05 }}
    >
      <Link
        to={`/etapes/${s.id}`}
        data-testid={`stage-item-${s.id}`}
        className="bento-tile block bg-white border border-slate-200 rounded-2xl p-5 hover:border-[color:var(--coral)]"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[color:var(--ocean)]/20 to-[color:var(--coral)]/20 flex flex-col items-center justify-center flex-shrink-0">
              <div className="text-[9px] uppercase font-bold text-slate-500 leading-none">Ét.</div>
              <div className="font-display font-black text-xl text-[color:var(--deep)] leading-none mt-0.5">
                {s.stage_number}
              </div>
            </div>
            <div className="min-w-0">
              <div className="font-display font-extrabold text-lg text-[color:var(--deep)] truncate">
                {s.name}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {s.start_village} → {s.end_village}
              </div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <div className="text-[11px] font-mono text-slate-400">
                  {formatMartiniqueDate(s.date, { weekday: "short", month: "short" })}
                </div>
                {s.status === "upcoming" && !cd.closed && (
                  <div className={`text-[11px] font-mono font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
                    cd.urgent ? "bg-red-50 text-[color:var(--coral)]" : "bg-slate-100 text-slate-600"
                  }`}>
                    <Timer className="h-3 w-3" /> {cd.label}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className={`${STATUS_LABEL[s.status].cls} border-0 font-bold text-[10px] px-2.5 py-1 rounded-full`}>
              {STATUS_LABEL[s.status].text}
            </Badge>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
